import { AnalyticsEvent, logAnalyticsEvent } from 'utils/analytics'
import { ConversationStatus, ConversationType } from 'models/conversations'

import { FC } from 'react'
import { GRAY_700 } from 'constants/styling/theme'
import Modal from 'components/common/Modals/Modal/Modal'
import { MUIButton } from 'components/common/MUIButton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import { logger } from 'utils/logging'
import { useTranslation } from 'react-i18next'
import { useUpdateConversationStatus } from 'dataQueries/conversations.query'

// Above the floating widget container (1300) and the expanded MUI drawer (1200) so the confirmation
// dialog stacks on top of the panel instead of behind it.
const CONFIRM_MODAL_Z_INDEX = 1400

export type CloseConversationSource = 'header_menu' | 'close_prompt'

interface Props {
  conversationId: string
  conversationType?: ConversationType
  source: CloseConversationSource
  isOpen: boolean
  onCancel: () => void
  onClosed: () => void
}

export const CloseConversationDialog: FC<Props> = ({ conversationId, conversationType, source, isOpen, onCancel, onClosed }) => {
  const { t } = useTranslation(['chat'])
  const updateStatus = useUpdateConversationStatus()

  const confirmClose = () => {
    updateStatus.mutate(
      { conversationId, status: ConversationStatus.CLOSED },
      {
        onSuccess: () => {
          logAnalyticsEvent(AnalyticsEvent.CHAT_WIDGET_CONVERSATION_CLOSED, { conversationType: conversationType ?? null, source })
          onClosed()
        },
        onError: (error) => {
          logger.error('Failed to close conversation', error, {
            context: { component: 'CloseConversationDialog', action: 'confirmClose', metadata: { conversationId, source } },
          })
        },
      }
    )
  }

  return (
    <Modal
      isOpen={isOpen}
      title={t('chat:close_dialog.title')}
      zIndex={CONFIRM_MODAL_Z_INDEX}
      onClose={onCancel}
      onClickOutside={onCancel}
      footerContent={
        <Stack direction="row" justifyContent="flex-end" gap="1.2rem">

          <MUIButton type="secondaryBorder" onClick={onCancel}>
            {t('chat:close_dialog.cancel')}
          </MUIButton>

          <MUIButton type="defaultPrimary" isLoading={updateStatus.isPending} onClick={confirmClose}>
            {t('chat:close_dialog.confirm')}
          </MUIButton>

        </Stack>
      }
    >
      <Typography variant="text-md" color={GRAY_700}>
        {t('chat:close_dialog.description')}
      </Typography>
    </Modal>
  )
}
